import React from 'react';
import { useApi } from '../../context/api-context/use-api';
import { getInstance, getIsMaInstalled, getIsMobile, isSeo as getIsSeo, } from '../../../common/store/base-params/base-params-selectors';
import { WixRicosViewer as Viewer } from '@wix/ricos-viewer';
import { useConfiguration } from '../../context/configuration-context/use-configuration';
import { initViewerPlugins, getTransformedContent } from './ricos-utils';
import { getCssOverrides } from './ricos-styles';
import withError from '../../hoc/with-error';
import TextExtended from '../text-extended/text-extended';
import { useTranslate } from '../../context/api-context/translations/use-translate';
import { SHOW_MORE_BTN } from '../../../common/constants/data-hooks';
import classNames from 'classnames';
import { ArrowDownIcon, ArrowUpIcon } from '../icons/arrow-icon';
import styles from './ricos-viewer.scss';
import MemberCardLazy from '../member-card/member-card-lazy';
const isResizeObserverSupported = () => window && typeof window.ResizeObserver !== 'undefined';
const RicosViewer = withError(({ content, parentClass, onShowMoreClick, disableShowMore = false, contentId }) => {
    const [experiments, ricosBiParams, instance, locale, isMobile, isSeo, isMAInstalled, host] = useApi((state, actions, host) => {
        var _a;
        return [
            state.experiments,
            state.baseParams.biMetadata,
            getInstance(state),
            (_a = state.baseParams) === null || _a === void 0 ? void 0 : _a.language,
            getIsMobile(state),
            getIsSeo(state),
            getIsMaInstalled(state),
            host,
        ];
    });
    const { pluginConfig, ricosTheming, contentTruncationLimit, transformCommentOnRender } = useConfiguration();
    const t = useTranslate();
    const viewerRef = React.useRef(null);
    const [hoveredMentionData, setHoverMentionData] = React.useState(null);
    const plugins = React.useMemo(() => initViewerPlugins((pluginConfig === null || pluginConfig === void 0 ? void 0 : pluginConfig.type) === 'async' ? pluginConfig.plugins : pluginConfig, {
        setHoverMentionData,
    }), [pluginConfig]);
    const contentAfterConsumerTransform = transformCommentOnRender && content ? transformCommentOnRender({ content }) : content;
    const transformedContent = React.useMemo(() => getTransformedContent(contentAfterConsumerTransform, {
        truncationThreshold: contentTruncationLimit,
    }), [contentAfterConsumerTransform, contentTruncationLimit]);
    const [viewerHeight, setViewerHeight] = React.useState('auto');
    const [viewerState, setViewerState] = React.useState(() => transformedContent.truncatedContent ? 'TruncatedCollapsed' : 'NonTruncated');
    React.useEffect(() => {
        if (viewerRef.current && transformedContent.truncatedContent && isResizeObserverSupported()) {
            // using observer here as we might need to update viewer height few times, as media loads and height changes
            const observer = new window.ResizeObserver((entries) => {
                setViewerHeight(entries[0].contentRect.height);
            });
            observer.observe(viewerRef.current);
            return () => {
                observer.disconnect();
            };
        }
    }, []);
    return (React.createElement("div", { className: styles.container },
        isMAInstalled && hoveredMentionData && hoveredMentionData.ref && (React.createElement(MemberCardLazy, { key: hoveredMentionData.mention.id, viewedMemberId: hoveredMentionData.mention.id, viewedMemberSlug: hoveredMentionData.mention.slug, elementRef: hoveredMentionData.ref })),
        React.createElement("div", { className: classNames(styles.viewer, 'wc-content-font', {
                [styles.truncated]: viewerState === 'TruncatedCollapsed',
                [styles.expandTransition]: viewerState === 'TruncatedExpanded',
                [styles.shrinkTransition]: viewerState === 'TruncatedCollapsed',
            }), style: { height: viewerHeight } },
            React.createElement("div", { ref: viewerRef },
                React.createElement(Viewer, { wixExperiments: experiments, plugins: plugins, content: viewerState === 'TruncatedCollapsed'
                        ? transformedContent.truncatedContent
                        : transformedContent.content, locale: locale, isMobile: isMobile, seoSettings: isSeo, theme: Object.assign(Object.assign({ palette: host.style.siteColors }, ricosTheming === null || ricosTheming === void 0 ? void 0 : ricosTheming.theme), { parentClass }), instance: instance, biSettings: {
                        consumer: 'wixComments',
                        platform: 'Livesite',
                        defaultParams: ricosBiParams,
                        contentId,
                    }, cssOverride: Object.assign(Object.assign({}, ricosTheming === null || ricosTheming === void 0 ? void 0 : ricosTheming.cssOverride), getCssOverrides(isMobile, 'viewer')), linkSettings: { relValue: 'nofollow ugc' } }))),
        viewerState === 'NonTruncated' || disableShowMore ? null : (React.createElement("button", { "data-hook": SHOW_MORE_BTN, className: classNames(styles.showMore, 'wc-button-secondary-icon-fill'), onClick: () => {
                onShowMoreClick === null || onShowMoreClick === void 0 ? void 0 : onShowMoreClick(viewerState === 'TruncatedCollapsed');
                setViewerState(viewerState === 'TruncatedCollapsed' ? 'TruncatedExpanded' : 'TruncatedCollapsed');
            } },
            React.createElement(TextExtended, { typography: "content", priority: "buttonSecondary" }, t(viewerState === 'TruncatedExpanded' ? 'comment.show-less' : 'comment.show-more')),
            viewerState === 'TruncatedExpanded' ? React.createElement(ArrowUpIcon, null) : React.createElement(ArrowDownIcon, null)))));
});
RicosViewer.displayName = 'RicosViewer';
export default RicosViewer;
//# sourceMappingURL=ricos-viewer.js.map